import { Link } from 'react-router-dom';
import { ArrowRight, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { industries } from '@/data/mock';
import { useScrollReveal } from '@/hooks/useScrollReveal';

const Reveal = ({ children, className = '', delay = 0 }) => {
  const [ref, isVisible] = useScrollReveal();
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(24px)',
        transition: `opacity 0.7s cubic-bezier(0.16,1,0.3,1) ${delay}ms, transform 0.7s cubic-bezier(0.16,1,0.3,1) ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
};

export default function IndustriesPage() {
  return (
    <div>
      {/* Header */}
      <section className="bg-[#0B1D33] pt-32 pb-20 lg:pt-36 lg:pb-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <p className="text-[#3B82A0] text-[11px] font-medium uppercase tracking-[0.3em] mb-4">
            Sectors We Serve
          </p>
          <h1 className="text-3xl md:text-4xl font-light text-white mb-5 tracking-tight">
            Industries
          </h1>
          <p className="text-slate-300/80 text-lg max-w-2xl leading-relaxed">
            Deep domain expertise across the most security-sensitive and heavily regulated industries,
            delivering advisory that accounts for sector-specific risk and compliance requirements.
          </p>
        </div>
      </section>

      {/* Industries Grid */}
      <section className="bg-[#F8FAFC] py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {industries.map((industry, i) => (
              <Reveal key={industry.id} delay={i * 100}>
                <div className="bg-white rounded-lg border border-slate-200/80 overflow-hidden h-full hover:shadow-md transition-shadow duration-300">
                  {industry.image && (
                    <div className="relative h-44 overflow-hidden">
                      <img
                        src={industry.image}
                        alt={industry.title}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0B1D33]/60 via-[#0B1D33]/15 to-transparent" />
                      <h2 className="absolute bottom-4 left-6 text-lg font-semibold text-white drop-shadow-sm">
                        {industry.title}
                      </h2>
                    </div>
                  )}
                  <div className="p-8 lg:p-10">
                    <p className="text-slate-500 leading-relaxed mb-6">
                      {industry.description}
                    </p>
                    <div>
                      <h4 className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-400 mb-3">
                        Key Focus Areas
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {industry.challenges.map((challenge) => (
                          <span
                            key={challenge}
                            className="text-xs font-medium text-slate-600 bg-slate-50 border border-slate-200/80 rounded px-3 py-1.5"
                          >
                            {challenge}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Statement */}
      <section className="bg-white py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal>
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-2xl md:text-3xl font-light text-[#0B1D33] mb-6 tracking-tight">
                Trusted Across Regulated Industries
              </h2>
              <p className="text-slate-500 text-lg leading-relaxed mb-10">
                Our advisory team brings direct operational experience within the industries we serve.
                We understand not just the regulatory requirements, but the business context, operational
                constraints, and stakeholder dynamics that shape effective security strategy.
              </p>
              <Link to="/contact">
                <Button className="bg-[#0B1D33] hover:bg-[#162D4A] text-white px-8 h-11 text-sm font-medium rounded transition-colors duration-200">
                  Discuss Your Industry Needs
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
